import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  sendEmailVerification,
  applyActionCode
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  getCountFromServer 
} from 'firebase/firestore';
import { logger, logFirebaseError } from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [justRegistered, setJustRegistered] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // 🔥 Ref pour la dernière activité (évite des re-renders à chaque mouvement)
  const lastActivityRef = React.useRef(Date.now());

  // Configuration du timeout (30 minutes d'inactivité)
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
  const WARNING_BEFORE_TIMEOUT = 2 * 60 * 1000;

  // Écouter les changements d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      logger.log('🔔 Auth state changed, firebaseUser =', firebaseUser?.uid);

      if (firebaseUser) {
        if (justRegistered) {
          logger.log('✅ Inscription détectée, attente 2 secondes avant déconnexion');
          await new Promise(resolve => setTimeout(resolve, 2000));
          logger.log('🔓 Déconnexion de l\'utilisateur non approuvé');
          setJustRegistered(false);
          await signOut(auth);
          setCurrentUser(null);
          setIsLoading(false);
          return;
        }
        
        try {
          logger.log('🔍 Récupération document Firestore pour UID:', firebaseUser.uid);
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            logger.log('📄 Document trouvé:', userData);
            
            // Vérifier si l'utilisateur est approuvé
            if (userData.approved === true || userData.status === 'approved') {
              logger.log('✅ Utilisateur approuvé, connexion réussie');
              
              setCurrentUser({
                uid: firebaseUser.uid,
                id: firebaseUser.uid,
                email: firebaseUser.email,
                emailVerified: firebaseUser.emailVerified,
                ...userData
              });
              lastActivityRef.current = Date.now();
            } else {
              logger.log('❌ Utilisateur non approuvé');
              await signOut(auth);
              setCurrentUser(null);
            }
          } else {
            logger.log('❌ Document utilisateur introuvable dans Firestore');
            await signOut(auth);
            setCurrentUser(null);
          }
        } catch (error) {
          logger.error('❌ Erreur lors de la récupération des données utilisateur:', error);
          setCurrentUser(null);
        }
      } else {
        logger.log('👤 Pas d\'utilisateur authentifié');
        setCurrentUser(null);
      }
      setIsLoading(false);
    });

    return unsubscribe;
  }, [justRegistered]);

  // Surveillance de l'inactivité et timeout de session (version optimisée)
  useEffect(() => {
    if (!currentUser) return;

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      setShowTimeoutWarning(false);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });

    const checkInactivity = setInterval(() => {
      const now = Date.now();
      const inactiveTime = now - lastActivityRef.current;

      if (
        inactiveTime >= INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT &&
        inactiveTime < INACTIVITY_TIMEOUT &&
        !showTimeoutWarning
      ) {
        setShowTimeoutWarning(true);
        logger.log('⚠️ Avertissement: Déconnexion dans 2 minutes');
      }

      if (inactiveTime >= INACTIVITY_TIMEOUT) {
        logger.log('⏱️ Session expirée - déconnexion automatique');
        logout();
      }
    }, 10000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      clearInterval(checkInactivity);
    };
  }, [currentUser, showTimeoutWarning]);

  // 🔐 LOGIN SANS 2FA
  const login = async (email, password) => {
    try {
      logger.log('🔐 Tentative de connexion:', email);
      
      // 1. Connexion avec email/password
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      logger.log('✅ Authentification Firebase réussie');

      // 2. Récupérer les données utilisateur depuis Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        logger.error('❌ Document utilisateur non trouvé dans Firestore');
        await signOut(auth);
        return { 
          success: false, 
          error: 'Compte introuvable dans la base de données' 
        };
      }

      const userData = userDoc.data();
      logger.log('📊 Données utilisateur récupérées:', {
        approved: userData.approved
      });

      // 3. Vérifier si le compte est approuvé
      if (userData.status === 'pending' || userData.approved === false) {
        logger.warn('⚠️ Compte non approuvé');
        await signOut(auth);
        return { 
          success: false, 
          error: 'Votre compte est en attente d\'approbation par un administrateur.' 
        };
      }

      // 4. Mettre à jour la dernière connexion (plus de 2FA ici)
      await setDoc(doc(db, 'users', user.uid), {
        ...userData,
        lastLogin: new Date().toISOString()
      }, { merge: true });

      logger.log('✅ Connexion réussie (2FA désactivée)');
      return { 
        success: true,
        user: {
          id: user.uid,
          uid: user.uid,
          email: user.email,
          ...userData
        }
      };
    } catch (error) {
      logger.error('❌ Erreur de connexion:', error);
      
      let errorMessage = 'Email ou mot de passe incorrect';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Aucun compte trouvé avec cet email';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Mot de passe incorrect';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Format d\'email invalide';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Trop de tentatives. Veuillez réessayer plus tard';
      }

      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  // Déconnexion
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      return { success: true };
    } catch (error) {
      logger.error('Erreur de déconnexion:', error);
      return { 
        success: false, 
        error: 'Erreur lors de la déconnexion' 
      };
    }
  };

  // Inscription
  const register = async (userData) => {
    try {
      logger.log('🟢 AuthContext: Début register');
      
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );

      const user = userCredential.user;
      logger.log('🟢 AuthContext: Compte Firebase Auth créé, UID =', user.uid);

      // Vérifier s'il s'agit du premier utilisateur
      const usersSnapshot = await getCountFromServer(collection(db, 'users'));
      const isFirstUser = usersSnapshot.data().count === 0;
      
      logger.log('🟢 AuthContext: isFirstUser =', isFirstUser);

      // Créer le document utilisateur dans Firestore
      const newUserData = {
        email: userData.email,
        name: userData.name,
        phone: userData.phone || '',
        role: isFirstUser ? 'admin' : 'membre',
        approved: isFirstUser,
        status: isFirstUser ? 'approved' : 'pending',
        createdAt: new Date().toISOString(),
        emailVerified: user.emailVerified,
        twoFactorEnabled: false // champ laissé pour compat compatibilité BDD, mais toujours false
      };

      logger.log('🟢 AuthContext: Données utilisateur à créer:', newUserData);

      await setDoc(doc(db, 'users', user.uid), newUserData);
      logger.log('🟢 AuthContext: Document Firestore créé');

      if (!isFirstUser) {
        logger.log('🟢 AuthContext: Utilisateur non premier, activation flag justRegistered');
        setJustRegistered(true);
      }

      logger.log('🟢 AuthContext: Register terminé avec succès');
      return { 
        success: true,
        isFirstUser,
        message: isFirstUser 
          ? 'Compte administrateur créé avec succès' 
          : 'Votre demande d\'inscription a été envoyée. Un administrateur doit approuver votre compte.'
      };
    } catch (error) {
      logger.error('❌ AuthContext: Erreur lors de l\'inscription:', error);
      logFirebaseError(error, 'AuthContext.register');
      
      let errorMessage = 'Erreur lors de l\'inscription';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Cette adresse email est déjà utilisée';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Le mot de passe doit contenir au moins 6 caractères';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Format d\'email invalide';
      }

      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  // Changer le mot de passe
  const changePassword = async (currentPassword, newPassword) => {
    if (!auth.currentUser) {
      return { success: false, error: 'Aucun utilisateur connecté' };
    }

    try {
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword
      );

      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      
      logger.log('✅ Mot de passe modifié avec succès');
      return { success: true, message: 'Mot de passe modifié avec succès' };
    } catch (error) {
      logger.error('❌ Erreur changement de mot de passe:', error);
      
      let errorMessage = 'Erreur lors du changement de mot de passe';
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Mot de passe actuel incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Le nouveau mot de passe doit contenir au moins 6 caractères';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Mettre à jour l'utilisateur courant
  const updateCurrentUser = async (updatedData) => {
    if (!currentUser) {
      return { success: false, error: 'Aucun utilisateur connecté' };
    }

    try {
      await setDoc(doc(db, 'users', currentUser.id), updatedData, { merge: true });
      
      const updated = { ...currentUser, ...updatedData };
      setCurrentUser(updated);
      
      return { success: true };
    } catch (error) {
      logger.error('Erreur de mise à jour:', error);
      return { 
        success: false, 
        error: 'Erreur lors de la mise à jour du profil' 
      };
    }
  };

  // Réinitialiser le mot de passe (email)
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin + '/login',
        handleCodeInApp: false
      });
      logger.log('✅ Email de réinitialisation envoyé à:', email);
      return { 
        success: true, 
        message: 'Un email de réinitialisation a été envoyé à votre adresse' 
      };
    } catch (error) {
      logger.error('❌ Erreur réinitialisation mot de passe:', error);
      
      let errorMessage = 'Erreur lors de l\'envoi de l\'email';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Aucun compte trouvé avec cet email';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Format d\'email invalide';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Trop de tentatives. Veuillez réessayer plus tard';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Envoyer un email de vérification
  const sendVerificationEmail = async () => {
    if (!auth.currentUser) {
      return { success: false, error: 'Aucun utilisateur connecté' };
    }

    try {
      await sendEmailVerification(auth.currentUser, {
        url: window.location.origin,
        handleCodeInApp: true
      });
      logger.log('✅ Email de vérification envoyé');
      return { 
        success: true, 
        message: 'Email de vérification envoyé. Veuillez vérifier votre boîte de réception' 
      };
    } catch (error) {
      logger.error('❌ Erreur envoi email vérification:', error);
      
      let errorMessage = 'Erreur lors de l\'envoi de l\'email';
      if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Trop de tentatives. Veuillez réessayer plus tard';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Vérifier un code d'action email
  const verifyEmailCode = async (code) => {
    try {
      await applyActionCode(auth, code);
      
      if (auth.currentUser) {
        await auth.currentUser.reload();
        setCurrentUser(prev => ({
          ...prev,
          emailVerified: auth.currentUser.emailVerified
        }));
      }
      
      logger.log('✅ Email vérifié avec succès');
      return { success: true, message: 'Email vérifié avec succès' };
    } catch (error) {
      logger.error('❌ Erreur vérification email:', error);
      
      let errorMessage = 'Code de vérification invalide ou expiré';
      if (error.code === 'auth/expired-action-code') {
        errorMessage = 'Le code de vérification a expiré';
      } else if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'Code de vérification invalide';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Garder la session active (par ex. via un ping)
  const keepSessionAlive = () => {
    lastActivityRef.current = Date.now();
    setShowTimeoutWarning(false);
  };

  const value = {
    currentUser,
    isLoading,
    login,
    logout,
    register,
    changePassword,
    updateCurrentUser,
    resetPassword,
    sendVerificationEmail,
    verifyEmailCode,
    keepSessionAlive,
    showTimeoutWarning,
    isAuthenticated: !!currentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;